import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { ownerIdZodSchema } from "@@/shared/owner-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { discountConditionZodSchema } from "../conditions/discount-condition";

import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { percentageZodSchema } from "@@/shared/percentage";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { discountCodeBulkIdZodSchema } from "./discount-code-bulk-id";
import {
    discountCodeIdFactory,
    discountCodeIdZodSchema,
} from "./discount-code-id";
import { discountCodeZodString } from "./discount-code-string";
import { discountCodeUsageLimitZodObject } from "./discount-code-usage-limits";

const discountCodeZodObject = z.object({
    _id: discountCodeIdZodSchema.default(() => discountCodeIdFactory()),
    _type: z.literal("DISCOUNT_CODE").default("DISCOUNT_CODE"),
    _createdAt: isoAndUnixZodSchema.default(() => nowIsoAndUnix()),
    _status: entityStateZodSchema.default("ACTIVE"),
    _createdBy: userIdZodSchema,
    meta: metaContentZodSchema,
    ownerId: ownerIdZodSchema,
    validTimeRange: timeRangeZodSchema,
    conditions: z.array(discountConditionZodSchema),
    type: z.enum(["VALUE", "PERCENTAGE"]),
    value: percentageZodSchema.or(z.number().min(0)),
    code: discountCodeZodString,
    usage: discountCodeUsageLimitZodObject,
    bulk: z
        .object({
            bulkId: discountCodeBulkIdZodSchema,
            bulkIndex: z.number().int().min(0),
            bulkPrefix: discountCodeZodString,
            bulkCount: z.number().int().min(1),
        })
        .optional(),
});

type DiscountCode = z.infer<typeof discountCodeZodObject>;
type DiscountCodeWithBulk = Except<DiscountCode, "bulk"> & {
    bulk: Required<DiscountCode>["bulk"];
};
type DiscountCodeWithoutBulk = Except<DiscountCode, "bulk"> & {
    bulk?: never;
};

type FactoryParams = Except<
    DiscountCode,
    "_id" | "_createdAt" | "_type" | "_status"
>;
const discountCodeFactory = (params: FactoryParams): DiscountCode => {
    return discountCodeZodObject.parse(params);
};

export { discountCodeFactory, discountCodeZodObject };
export type { DiscountCode, DiscountCodeWithBulk, DiscountCodeWithoutBulk };
